import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { StorageLocationType } from "@redotech/crdb-enums/fulfillment/storage-location-type";
import { WarehouseItemWorkRequestKind } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-kind";
import { InventoryItemKind } from "@redotech/crdb-enums/inventory/inventory-item-kind";
import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import { InventoryRuleActionType } from "@redotech/crdb-enums/inventory/inventory-rule-action-type";
import { ReturnVerificationResult } from "@redotech/crdb-enums/returns/return-verification-result";
import { accountIdFormat } from "@redotech/redo-account-id";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { t } from "@redotech/redo-i18n";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { storageLocationIdFormat } from "@redotech/redo-warehouse-id";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type { InventoryRuleRunnableActionType } from "./rule-definition";

export const inventoryRuleFactSchema = createEnum([
  "kind",
  "has_product",
  "has_return",
  "has_return_item",
  "has_lot",
  "has_source_receiving_bin",
  "grade",
  "sku",
  "account",
  "is_recommerce",
  "processing_level",
  "location",
  "location_kind",
  "storage_location",
  "storage_location_type",
  "processing_state",
  "processed_by",
  "verification_result",
  "outcome",
  "open_service_request_kind",
  "product_tags",
  "assign_outcome_status",
  "assign_outcome_count",
  "transform_product_status",
  "transform_product_count",
  "list_on_channel_status",
  "list_on_channel_count",
  "applied_rules",
]);
export type InventoryRuleFact = z.infer<typeof inventoryRuleFactSchema>;

export const inventoryRuleOperatorSchema = createEnum([
  "is",
  "in",
  "not_in",
  "contains_any",
  "contains_all",
  "contains_none",
  "is_empty",
  "is_not_empty",
  "equals",
  "not_equals",
  "greater_than",
  "greater_than_or_equal_to",
  "less_than",
  "less_than_or_equal_to",
]);
export type InventoryRuleOperator = z.infer<typeof inventoryRuleOperatorSchema>;

/** Shape only; `validateInventoryRuleConditions` checks it fits the fact and operator. */
const inventoryRuleConditionValueSchema = z.union([
  z.boolean(),
  z.number(),
  z.array(z.string()),
  z.null(),
]);
const inventoryRuleConditionLeafSchema = z.object({
  nodeType: z.literal("leaf"),
  fact: inventoryRuleFactSchema,
  operator: inventoryRuleOperatorSchema,
  value: inventoryRuleConditionValueSchema,
});
export type InventoryRuleConditionLeaf = z.infer<
  typeof inventoryRuleConditionLeafSchema
>;

/** AND needs every child, OR any child; `negated` inverts the group's result. */
export interface InventoryRuleConditionGroup {
  nodeType: "group";
  conjunctionMode: ConjunctionMode;
  negated?: boolean;
  children: InventoryRuleConditionNode[];
}

export type InventoryRuleConditionNode =
  | InventoryRuleConditionLeaf
  | InventoryRuleConditionGroup;

const groupFields = {
  nodeType: z.literal("group"),
  conjunctionMode: createEnum(ConjunctionMode),
  negated: z.boolean().optional(),
};

const inventoryRuleConditionGroupSchema: z.ZodType<InventoryRuleConditionGroup> =
  z.lazy(() =>
    z.object({
      ...groupFields,
      children: z.array(inventoryRuleConditionNodeSchema).min(1),
    }),
  );

export const inventoryRuleConditionNodeSchema: z.ZodType<InventoryRuleConditionNode> =
  z.lazy(() =>
    z.union([
      inventoryRuleConditionLeafSchema,
      inventoryRuleConditionGroupSchema,
    ]),
  );

/**
 * The root group, the BOM product conditions tree plus negation. Only a plain
 * AND root may be empty, as the catch-all; an empty OR or negated group matches nothing.
 */
export const inventoryRuleConditionsSchema: z.ZodType<InventoryRuleConditionGroup> =
  z
    .object({
      ...groupFields,
      children: z.array(inventoryRuleConditionNodeSchema),
    })
    .refine(
      (root) =>
        root.children.length > 0 ||
        (root.conjunctionMode === ConjunctionMode.AND && !root.negated),
      { message: "Only a plain AND root group may be empty" },
    );
export type InventoryRuleConditions = InventoryRuleConditionGroup;

const inventoryRuleLocationKinds = [
  "returns_center",
  "merchant_warehouse",
] as const;
export type InventoryRuleLocationKind =
  (typeof inventoryRuleLocationKinds)[number];

export function locationKind(
  returnsCenterId: bigint | null,
): InventoryRuleLocationKind {
  return returnsCenterId === null ? "merchant_warehouse" : "returns_center";
}

/** The storage location types a condition can name. */
export const INVENTORY_RULE_STORAGE_LOCATION_TYPES: readonly StorageLocationType[] =
  [
    StorageLocationType.Bin,
    StorageLocationType.Tote,
    StorageLocationType.SortingTote,
  ];

/** Decided in order: an open session holds it, a session processed it, its level needs none, else it waits. */
const processingStates = [
  "in_processing",
  "processed",
  "no_processing_required",
  "awaiting_processing",
] as const;
export type InventoryItemProcessingState = (typeof processingStates)[number];

/** Which kind of session processed the item last. */
const processedByValues = ["return", "item", "none"] as const;
export type InventoryItemProcessedBy = (typeof processedByValues)[number];

/** The return's latest warehouse verification; none without a return or a verification. */
const verificationResults = [
  "none",
  ...Object.values(ReturnVerificationResult),
] as const;
export type InventoryItemVerificationResult =
  (typeof verificationResults)[number];

const itemActionStatuses = ["none", "applied", "skipped"] as const;
/** The item's latest current (not superseded) action row of a type, or none. */
type ItemActionStatus = (typeof itemActionStatuses)[number];

export interface ItemActionFacts {
  status: ItemActionStatus;
  /** Current applied rows of the type. */
  appliedCount: number;
}

/** Everything a condition can test about one inventory item. */
export interface InventoryItemFacts {
  kind: InventoryItemKind;
  hasProduct: boolean;
  hasReturn: boolean;
  hasReturnItem: boolean;
  hasLot: boolean;
  /** Received from an inbound shipment's receiving bin; a return item can have one too. */
  hasSourceReceivingBin: boolean;
  grade: string | null;
  sku: string | null;
  /** The item's current account: the merchant's, or its connected recommerce account after a transform. */
  accountId: bigint;
  isRecommerce: boolean;
  /** The merchant's level at the location's returns center, else its account-wide level, else the default. */
  processingLevel: ProcessingLevel;
  /**
   * The location the rule sees the item at: where it was processed for
   * processed rules, else where its stock is.
   */
  locationId: bigint;
  locationKind: InventoryRuleLocationKind;
  /** The location's returns center, for its supported outcomes; no condition tests it. */
  returnsCenterId: bigint | null;
  /** Where the item's live stock sits; empty without any. */
  storageLocationIds: readonly bigint[];
  storageLocationTypes: readonly StorageLocationType[];
  processingState: InventoryItemProcessingState;
  processedBy: InventoryItemProcessedBy;
  verificationResult: InventoryItemVerificationResult;
  outcome: InventoryItemOutcome | null;
  openServiceRequestKinds: readonly WarehouseItemWorkRequestKind[];
  productTags: readonly string[];
  /** Read for the item's merchant. */
  actions: Readonly<Record<InventoryRuleRunnableActionType, ItemActionFacts>>;
  /** Rules with a current applied row for the item, as `inr_` ids. */
  appliedRuleIds: readonly string[];
}

export type InventoryRuleFactType =
  | "boolean"
  | "enum"
  | "string"
  | "account"
  | "location"
  | "storage_location"
  | "set"
  | "number";

/** Where the condition builder gets the values a condition can name. */
export type InventoryRuleFactValueSource =
  /** The fact's `domain`. */
  | "domain"
  | "accounts"
  | "locations"
  | "storage_locations"
  | "grades"
  | "product_tags"
  /** Typed or toggled in the condition itself: booleans, numbers and free text. */
  | "entered";

interface FactDefinitionBase {
  label: () => string;
  /** Written only by the API, as lane branch conditions; the condition builder can't name it. */
  internal?: boolean;
  /** Narrows the type's operators. */
  operators?: readonly InventoryRuleOperator[];
  /** Lowercase both sides before comparing. */
  caseInsensitive?: boolean;
  /** Set on facts read from `InventoryItemFacts.actions`. */
  action?: {
    type: InventoryRuleRunnableActionType;
    field: keyof ItemActionFacts;
  };
}

/**
 * A fact that loads several values, such as where an item's stock sits,
 * matches `in` when any of them is named. No values counts as empty.
 */
export type InventoryRuleFactDefinition = FactDefinitionBase &
  (
    | {
        type: "boolean";
        valueSource: "entered";
        load: (facts: InventoryItemFacts) => boolean;
      }
    | {
        type: "enum";
        valueSource: "domain";
        domain: readonly string[];
        /** Set on domains defined here; database enum domains use their existing labels. */
        valueLabels?: Readonly<Record<string, () => string>>;
        load: (facts: InventoryItemFacts) => string | null | readonly string[];
      }
    | {
        type: "string";
        valueSource: "grades" | "entered";
        load: (facts: InventoryItemFacts) => string | null;
      }
    | {
        type: "account";
        valueSource: "accounts";
        load: (facts: InventoryItemFacts) => bigint;
      }
    | {
        type: "location";
        valueSource: "locations";
        load: (facts: InventoryItemFacts) => bigint;
      }
    | {
        type: "storage_location";
        valueSource: "storage_locations";
        load: (facts: InventoryItemFacts) => readonly bigint[];
      }
    | {
        type: "set";
        valueSource: "domain" | "product_tags" | "entered";
        domain?: readonly string[];
        load: (facts: InventoryItemFacts) => readonly string[];
      }
    | {
        type: "number";
        valueSource: "entered";
        load: (facts: InventoryItemFacts) => number;
      }
  );

export const INVENTORY_RULE_OPERATORS_BY_TYPE: Readonly<
  Record<InventoryRuleFactType, readonly InventoryRuleOperator[]>
> = {
  boolean: ["is"],
  enum: ["in", "not_in", "is_empty", "is_not_empty"],
  string: ["in", "not_in", "is_empty", "is_not_empty"],
  account: ["in", "not_in"],
  location: ["in", "not_in"],
  storage_location: ["in", "not_in", "is_empty", "is_not_empty"],
  set: [
    "contains_any",
    "contains_all",
    "contains_none",
    "is_empty",
    "is_not_empty",
  ],
  number: [
    "equals",
    "not_equals",
    "greater_than",
    "greater_than_or_equal_to",
    "less_than",
    "less_than_or_equal_to",
  ],
};

function booleanFact(
  label: () => string,
  load: (facts: InventoryItemFacts) => boolean,
): InventoryRuleFactDefinition {
  return { type: "boolean", valueSource: "entered", label, load };
}

function enumFact<T extends string>(
  label: () => string,
  domain: readonly T[],
  load: (facts: InventoryItemFacts) => T | null | readonly T[],
  valueLabels?: Readonly<Record<T, () => string>>,
): InventoryRuleFactDefinition {
  return {
    type: "enum",
    valueSource: "domain",
    label,
    domain,
    load,
    ...(valueLabels && { valueLabels }),
  };
}

const LOCATION_KIND_LABELS: Readonly<
  Record<InventoryRuleLocationKind, () => string>
> = {
  returns_center: () => t("Returns center"),
  merchant_warehouse: () => t("Merchant warehouse"),
};

const PROCESSING_STATE_LABELS: Readonly<
  Record<InventoryItemProcessingState, () => string>
> = {
  in_processing: () => t("In processing"),
  processed: () => t("Processed"),
  no_processing_required: () => t("No processing required"),
  awaiting_processing: () => t("Awaiting processing"),
};

const PROCESSED_BY_LABELS: Readonly<
  Record<InventoryItemProcessedBy, () => string>
> = {
  return: () => t("Return", { context: "processed by" }),
  item: () => t("Item", { context: "processed by" }),
  none: () => t("Not processed"),
};

const VERIFICATION_RESULT_LABELS: Readonly<
  Record<InventoryItemVerificationResult, () => string>
> = {
  none: () => t("Not verified"),
  [ReturnVerificationResult.Pending]: () =>
    t("Pending", { context: "verification result" }),
  [ReturnVerificationResult.Pass]: () => t("Passed"),
  [ReturnVerificationResult.Fail]: () => t("Failed"),
};

const ACTION_STATUS_LABELS: Readonly<Record<ItemActionStatus, () => string>> = {
  none: () => t("Not applied"),
  applied: () => t("Applied"),
  skipped: () => t("Skipped"),
};

function actionStatusFact(
  label: () => string,
  type: InventoryRuleRunnableActionType,
): InventoryRuleFactDefinition {
  return {
    ...enumFact(
      label,
      itemActionStatuses,
      (facts) => facts.actions[type].status,
      ACTION_STATUS_LABELS,
    ),
    action: { type, field: "status" },
  };
}

function actionCountFact(
  label: () => string,
  type: InventoryRuleRunnableActionType,
): InventoryRuleFactDefinition {
  return {
    type: "number",
    valueSource: "entered",
    label,
    action: { type, field: "appliedCount" },
    load: (facts) => facts.actions[type].appliedCount,
  };
}

/**
 * Every way to identify an item, for inventory rules and sort destinations. `account` is the
 * merchant fact, since a destination covers many; age is the scheduled trigger, not a fact.
 */
export const INVENTORY_RULE_FACTS: Readonly<
  Record<InventoryRuleFact, InventoryRuleFactDefinition>
> = {
  kind: enumFact(
    () => t("Item kind"),
    Object.values(InventoryItemKind),
    (facts) => facts.kind,
  ),
  has_product: booleanFact(
    () => t("Has a product"),
    (facts) => facts.hasProduct,
  ),
  has_return: booleanFact(
    () => t("Has a return"),
    (facts) => facts.hasReturn,
  ),
  has_return_item: booleanFact(
    () => t("Has a return item"),
    (facts) => facts.hasReturnItem,
  ),
  has_lot: booleanFact(
    () => t("Has a lot"),
    (facts) => facts.hasLot,
  ),
  has_source_receiving_bin: booleanFact(
    () => t("Received from a receiving bin"),
    (facts) => facts.hasSourceReceivingBin,
  ),
  grade: {
    type: "string",
    valueSource: "grades",
    label: () => t("Grade"),
    load: (facts) => facts.grade,
  },
  sku: {
    type: "string",
    valueSource: "entered",
    label: () => t("SKU"),
    load: (facts) => facts.sku,
  },
  account: {
    type: "account",
    valueSource: "accounts",
    label: () => t("Merchant"),
    load: (facts) => facts.accountId,
  },
  is_recommerce: booleanFact(
    () => t("In a recommerce account"),
    (facts) => facts.isRecommerce,
  ),
  processing_level: enumFact(
    () => t("Processing level"),
    Object.values(ProcessingLevel),
    (facts) => facts.processingLevel,
  ),
  location: {
    type: "location",
    valueSource: "locations",
    label: () => t("Location"),
    load: (facts) => facts.locationId,
  },
  location_kind: enumFact(
    () => t("Location kind"),
    inventoryRuleLocationKinds,
    (facts) => facts.locationKind,
    LOCATION_KIND_LABELS,
  ),
  storage_location: {
    type: "storage_location",
    valueSource: "storage_locations",
    label: () => t("Storage location"),
    load: (facts) => facts.storageLocationIds,
  },
  storage_location_type: enumFact(
    () => t("Storage location type"),
    INVENTORY_RULE_STORAGE_LOCATION_TYPES,
    (facts) => facts.storageLocationTypes,
  ),
  processing_state: enumFact(
    () => t("Processing state"),
    processingStates,
    (facts) => facts.processingState,
    PROCESSING_STATE_LABELS,
  ),
  processed_by: enumFact(
    () => t("Processed by"),
    processedByValues,
    (facts) => facts.processedBy,
    PROCESSED_BY_LABELS,
  ),
  verification_result: enumFact(
    () => t("Verification result"),
    verificationResults,
    (facts) => facts.verificationResult,
    VERIFICATION_RESULT_LABELS,
  ),
  outcome: enumFact(
    () => t("Outcome"),
    Object.values(InventoryItemOutcome),
    (facts) => facts.outcome,
  ),
  open_service_request_kind: {
    type: "set",
    valueSource: "domain",
    label: () => t("Open service requests"),
    domain: Object.values(WarehouseItemWorkRequestKind),
    load: (facts) => facts.openServiceRequestKinds,
  },
  product_tags: {
    type: "set",
    valueSource: "product_tags",
    label: () => t("Product tags"),
    caseInsensitive: true,
    load: (facts) => facts.productTags,
  },
  assign_outcome_status: actionStatusFact(
    () => t("Assign outcome status"),
    InventoryRuleActionType.AssignOutcome,
  ),
  assign_outcome_count: actionCountFact(
    () => t("Times an outcome was assigned"),
    InventoryRuleActionType.AssignOutcome,
  ),
  transform_product_status: actionStatusFact(
    () => t("Transform product status"),
    InventoryRuleActionType.TransformProduct,
  ),
  transform_product_count: actionCountFact(
    () => t("Times the product was transformed"),
    InventoryRuleActionType.TransformProduct,
  ),
  list_on_channel_status: actionStatusFact(
    () => t("List on channel status"),
    InventoryRuleActionType.ListOnChannel,
  ),
  list_on_channel_count: actionCountFact(
    () => t("Times it was listed on a channel"),
    InventoryRuleActionType.ListOnChannel,
  ),
  applied_rules: {
    type: "set",
    valueSource: "entered",
    label: () => t("Applied rules"),
    internal: true,
    operators: ["contains_any", "contains_none"],
    load: (facts) => facts.appliedRuleIds,
  },
};

/** The operators a condition on the fact may use. */
export function inventoryRuleFactOperators(
  fact: InventoryRuleFact,
): readonly InventoryRuleOperator[] {
  const definition = INVENTORY_RULE_FACTS[fact];
  return (
    definition.operators ?? INVENTORY_RULE_OPERATORS_BY_TYPE[definition.type]
  );
}

/** A fact's value as conditions compare it; accounts and locations are their `acc_` and `loc_` ids. */
export type InventoryRuleFactValue =
  | boolean
  | number
  | string
  | null
  | readonly string[];

function loadInventoryRuleFact(
  fact: InventoryRuleFact,
  facts: InventoryItemFacts,
): InventoryRuleFactValue {
  const definition = INVENTORY_RULE_FACTS[fact];
  switch (definition.type) {
    case "account":
      return accountIdFormat.write(definition.load(facts));
    case "location":
      return locationIdFormat.write(definition.load(facts));
    case "storage_location":
      return definition
        .load(facts)
        .map((id) => storageLocationIdFormat.write(id));
    case "boolean":
    case "enum":
    case "string":
    case "set":
    case "number":
      return definition.load(facts);
  }
}

/** Whether a known fact value satisfies a condition. Malformed conditions never hold. */
export function conditionHolds(
  condition: InventoryRuleConditionLeaf,
  actual: InventoryRuleFactValue,
): boolean {
  const definition = INVENTORY_RULE_FACTS[condition.fact];
  const expected = condition.value;
  const normalize = (value: string) =>
    definition.caseInsensitive ? value.toLowerCase() : value;
  switch (condition.operator) {
    case "is":
      return typeof expected === "boolean" && actual === expected;
    case "in":
    case "not_in": {
      if (!Array.isArray(expected)) {
        return false;
      }
      const contained = valuesOf(actual).some((value) =>
        expected.some((wanted) => normalize(wanted) === normalize(value)),
      );
      return condition.operator === "in" ? contained : !contained;
    }
    case "contains_any":
    case "contains_all":
    case "contains_none": {
      if (!Array.isArray(expected) || !Array.isArray(actual)) {
        return false;
      }
      const present = new Set(actual.map(normalize));
      const wanted = expected.map(normalize);
      if (condition.operator === "contains_any") {
        return wanted.some((value) => present.has(value));
      }
      if (condition.operator === "contains_all") {
        return wanted.every((value) => present.has(value));
      }
      return wanted.every((value) => !present.has(value));
    }
    case "is_empty":
      return isEmpty(actual);
    case "is_not_empty":
      return !isEmpty(actual);
    case "equals":
    case "not_equals":
    case "greater_than":
    case "greater_than_or_equal_to":
    case "less_than":
    case "less_than_or_equal_to":
      return (
        typeof actual === "number" &&
        typeof expected === "number" &&
        compareNumbers(condition.operator, actual, expected)
      );
    default:
      return assertNever(condition.operator);
  }
}

/** The strings a fact holds: none for null, one for a string. */
function valuesOf(actual: InventoryRuleFactValue): readonly string[] {
  if (typeof actual === "string") {
    return [actual];
  }
  return Array.isArray(actual) ? actual : [];
}

/** No value: null, a blank string or no values. Booleans and numbers always have one. */
function isEmpty(actual: InventoryRuleFactValue): boolean {
  if (typeof actual === "string") {
    return actual.trim() === "";
  }
  return actual === null || (Array.isArray(actual) && actual.length === 0);
}

function compareNumbers(
  operator:
    | "equals"
    | "not_equals"
    | "greater_than"
    | "greater_than_or_equal_to"
    | "less_than"
    | "less_than_or_equal_to",
  actual: number,
  expected: number,
): boolean {
  switch (operator) {
    case "equals":
      return actual === expected;
    case "not_equals":
      return actual !== expected;
    case "greater_than":
      return actual > expected;
    case "greater_than_or_equal_to":
      return actual >= expected;
    case "less_than":
      return actual < expected;
    case "less_than_or_equal_to":
      return actual <= expected;
    default:
      return assertNever(operator);
  }
}

/** Three-valued truth: `maybe` when the answer depends on a fact that isn't known. */
export type Tri = boolean | "maybe";

function triAll(values: readonly Tri[]): Tri {
  if (values.includes(false)) {
    return false;
  }
  return values.every((value) => value === true) ? true : "maybe";
}

function triAny(values: readonly Tri[]): Tri {
  if (values.includes(true)) {
    return true;
  }
  return values.every((value) => value === false) ? false : "maybe";
}

function triNot(value: Tri): Tri {
  return value === "maybe" ? "maybe" : !value;
}

function evaluateNode(
  node: InventoryRuleConditionNode,
  evaluateLeaf: (leaf: InventoryRuleConditionLeaf) => Tri,
): Tri {
  if (node.nodeType === "leaf") {
    return evaluateLeaf(node);
  }
  const results = node.children.map((child) =>
    evaluateNode(child, evaluateLeaf),
  );
  const result =
    node.conjunctionMode === ConjunctionMode.AND
      ? triAll(results)
      : triAny(results);
  return node.negated ? triNot(result) : result;
}

/**
 * The one evaluator for rule conditions. `matches` runs it on a loaded item;
 * the coverage analyzer runs it on groups whose facts may be unknown.
 */
export function evaluateConditions(
  conditions: InventoryRuleConditions,
  evaluateLeaf: (leaf: InventoryRuleConditionLeaf) => Tri,
): Tri {
  return evaluateNode(conditions, evaluateLeaf);
}

/** Every leaf in the tree, depth first. */
export function conditionLeaves(
  node: InventoryRuleConditionNode,
): InventoryRuleConditionLeaf[] {
  return node.nodeType === "leaf"
    ? [node]
    : node.children.flatMap(conditionLeaves);
}

/** `AND[criteria, NOT each exclusion]`: a lane's rule, minus the items of the overrides ranked above it. */
export function withExclusions(
  criteria: InventoryRuleConditions,
  exclusions: readonly InventoryRuleConditions[],
): InventoryRuleConditions {
  if (exclusions.length === 0) {
    return criteria;
  }
  const negated = exclusions.map((exclusion): InventoryRuleConditionGroup => {
    if (exclusion.children.length === 0) {
      throw new Error("An exclusion needs at least one condition");
    }
    return { ...exclusion, negated: true };
  });
  return {
    nodeType: "group",
    conjunctionMode: ConjunctionMode.AND,
    children: isCatchAll(criteria) ? negated : [criteria, ...negated],
  };
}

/** Undoes `withExclusions`: the builder never negates a group, so a top-level negated group is an exclusion. */
export function splitExclusions(conditions: InventoryRuleConditions): {
  criteria: InventoryRuleConditions;
  exclusions: InventoryRuleConditions[];
} {
  const exclusions = isPlainAnd(conditions)
    ? conditions.children.flatMap((child) =>
        child.nodeType === "group" && child.negated === true ? [child] : [],
      )
    : [];
  if (exclusions.length === 0) {
    return { criteria: conditions, exclusions: [] };
  }
  const rest = conditions.children.filter((child) => !isNegatedGroup(child));
  const [only] = rest;
  return {
    criteria:
      rest.length === 1 && only?.nodeType === "group"
        ? only
        : {
            nodeType: "group",
            conjunctionMode: ConjunctionMode.AND,
            children: rest,
          },
    exclusions: exclusions.map(
      ({ negated: _negated, ...exclusion }) => exclusion,
    ),
  };
}

/**
 * Equal for criteria that match the same way, whatever their child order, value
 * order or exclusions, so a rule finds its settings row.
 */
export function inventoryRuleCriteriaKey(
  conditions: InventoryRuleConditions,
): string {
  return canonicalNode(splitExclusions(conditions).criteria);
}

function canonicalNode(node: InventoryRuleConditionNode): string {
  if (node.nodeType === "leaf") {
    return JSON.stringify([
      "leaf",
      node.fact,
      node.operator,
      canonicalValue(node),
    ]);
  }
  const children = [
    ...new Set(sameModeChildren(node).map(canonicalNode)),
  ].sort();
  const [only] = children;
  if (!node.negated && children.length === 1 && only !== undefined) {
    return only;
  }
  return JSON.stringify([
    "group",
    node.conjunctionMode,
    node.negated === true,
    children,
  ]);
}

/** Children, with nested plain groups of the same conjunction folded in: `AND[a, AND[b]]` is `AND[a, b]`. */
function sameModeChildren(
  group: InventoryRuleConditionGroup,
): InventoryRuleConditionNode[] {
  return group.children.flatMap((child) =>
    child.nodeType === "group" &&
    !child.negated &&
    child.conjunctionMode === group.conjunctionMode
      ? sameModeChildren(child)
      : [child],
  );
}

function canonicalValue(
  leaf: InventoryRuleConditionLeaf,
): InventoryRuleConditionLeaf["value"] {
  if (!Array.isArray(leaf.value)) {
    return leaf.value;
  }
  const normalize = INVENTORY_RULE_FACTS[leaf.fact].caseInsensitive
    ? (value: string) => value.toLowerCase()
    : (value: string) => value;
  return [...new Set(leaf.value.map(normalize))].sort();
}

export function isPlainAnd(group: InventoryRuleConditionGroup): boolean {
  return group.conjunctionMode === ConjunctionMode.AND && !group.negated;
}

function isCatchAll(group: InventoryRuleConditionGroup): boolean {
  return isPlainAnd(group) && group.children.length === 0;
}

export function isNegatedGroup(node: InventoryRuleConditionNode): boolean {
  return node.nodeType === "group" && node.negated === true;
}

export function matches(
  conditions: InventoryRuleConditions,
  facts: InventoryItemFacts,
): boolean {
  return (
    evaluateConditions(conditions, (leaf) =>
      conditionHolds(leaf, loadInventoryRuleFact(leaf.fact, facts)),
    ) === true
  );
}
